import React, { ReactNode, useContext, useEffect, useState } from 'react';

import UbiquitiHostISPMetricsContext from '@totem/components/ubiquiti/hosts/details/ispMetrics/ubiquitiHostISPMetricsContext';
import { UbiquitiISPMetric } from '@totem/components/ubiquiti/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { UBIQUITI_HOSTS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  hostId: string;
  children?: ReactNode;
};

// Standalone by design (takes hostId as a prop, not from route params) so it
// can be embedded anywhere a host's ISP metrics are needed, not just the
// details drill-down page.
const UbiquitiHostISPMetricsContainer = ({ hostId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [metrics, setMetrics] = useState<UbiquitiISPMetric[]>([]);
  const [duration, setDuration] = useState<string>('1');

  useEffect(() => {
    setLoading(true);

    setError(false);

    // Host IDs contain a literal colon (e.g. "D8B3...:1815601434"), which is
    // legal in a URL path segment per RFC 3986 but not guaranteed to survive
    // every intermediate proxy/gateway unencoded — encode defensively.
    fetch(
      `${UBIQUITI_HOSTS_ENDPOINT}/${encodeURIComponent(hostId)}/ispMetrics/${duration}`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => {
        // A non-2xx response body is an APIErrorResponse object, not a
        // metrics array — never hand that to the chart as its data source.
        if (!res.ok) {
          setError(true);
          return [];
        }
        return res.json();
      })
      .then((result: UbiquitiISPMetric[]) => {
        setMetrics(Array.isArray(result) ? result : []);
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError(true);

        setMetrics([]);

        setLoading(false);
      });
  }, [hostId, duration, getApiToken]);

  return (
    <UbiquitiHostISPMetricsContext.Provider
      value={{ loading, error, metrics, duration, setDuration }}
    >
      <div>{children}</div>
    </UbiquitiHostISPMetricsContext.Provider>
  );
};

export default UbiquitiHostISPMetricsContainer;
